import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GA_MEASUREMENT_ID,
  OPEN_PREFERENCES_EVENT,
  applyConsent,
  readConsent,
  saveConsent,
} from '@/utils/consent';

const HAS_ANALYTICS = GA_MEASUREMENT_ID !== '';

interface SwitchRowProps {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}

function SwitchRow({ id, title, description, checked, disabled, onChange }: SwitchRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="min-w-0">
        <label htmlFor={id} className="block font-body text-sm font-semibold text-gray-900 dark:text-white">
          {title}
        </label>
        <p id={id + '-desc'} className="mt-1 font-body text-xs leading-relaxed text-gray-600 dark:text-gray-300">
          {description}
        </p>
      </div>
      <div className="relative shrink-0 pt-0.5">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-describedby={id + '-desc'}
          onChange={(e) => onChange && onChange(e.target.checked)}
          className="peer sr-only"
        />
        <div
          aria-hidden="true"
          className="h-6 w-11 rounded-full bg-gray-300 dark:bg-gray-600 transition-colors peer-checked:bg-primary-600 peer-disabled:opacity-60 peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0.5 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"
        />
      </div>
    </div>
  );
}

export default function CookieConsent() {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [thirdParty, setThirdParty] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const current = readConsent();
    if (current) {
      applyConsent(current);
    } else {
      setBannerVisible(true);
    }
  }, []);

  const openPrefs = useCallback(() => {
    const current = readConsent();
    setAnalytics(current ? current.analytics : false);
    setThirdParty(current ? current.thirdParty : false);
    lastFocused.current = document.activeElement as HTMLElement | null;
    setPrefsOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener(OPEN_PREFERENCES_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, openPrefs);
  }, [openPrefs]);

  const closePrefs = useCallback(() => {
    setPrefsOpen(false);
    if (lastFocused.current && typeof lastFocused.current.focus === 'function') {
      lastFocused.current.focus();
    }
  }, []);

  useEffect(() => {
    if (!prefsOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const dialog = dialogRef.current;
    const focusables = (): HTMLElement[] =>
      dialog
        ? Array.from(
            dialog.querySelectorAll<HTMLElement>('button, a[href], input:not([disabled])')
          )
        : [];
    const first = focusables()[0];
    if (first) first.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePrefs();
        return;
      }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) return;
      const firstEl = list[0];
      const lastEl = list[list.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [prefsOpen, closePrefs]);

  const acceptAll = () => {
    saveConsent(HAS_ANALYTICS, true);
    setBannerVisible(false);
    setPrefsOpen(false);
  };

  const rejectNonEssential = () => {
    saveConsent(false, false);
    setBannerVisible(false);
    setPrefsOpen(false);
  };

  const savePrefs = () => {
    saveConsent(HAS_ANALYTICS && analytics, thirdParty);
    setBannerVisible(false);
    setPrefsOpen(false);
  };

  const btnBase =
    'inline-flex items-center justify-center rounded-full px-5 py-2.5 font-body text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900';

  return (
    <>
      {bannerVisible && !prefsOpen && (
        <div
          role="region"
          aria-label="Aviso de privacidade e cookies"
          className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4 pointer-events-none"
        >
          <div className="pointer-events-auto mx-auto max-w-4xl rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl p-5 sm:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
              <div className="flex-1">
                <h2 className="font-heading text-base font-bold text-gray-900 dark:text-white">
                  Sua privacidade importa
                </h2>
                <p className="mt-1.5 font-body text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  Usamos armazenamento essencial para o site funcionar. Com a sua permissão, também usamos
                  conteúdo de terceiros (como mapas do Google)
                  {HAS_ANALYTICS ? ' e ferramentas de análise de audiência' : ''}. Você escolhe: aceitar tudo,
                  recusar o que não é essencial ou personalizar. Saiba mais na{' '}
                  <Link to="/politica-privacidade" className="font-semibold underline hover:text-primary-700 dark:hover:text-primary">
                    Política de Privacidade
                  </Link>{' '}
                  e na{' '}
                  <Link to="/politica-cookies" className="font-semibold underline hover:text-primary-700 dark:hover:text-primary">
                    Política de Cookies
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row md:flex-col lg:flex-row md:shrink-0">
                <button
                  type="button"
                  onClick={rejectNonEssential}
                  className={btnBase + ' border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}
                >
                  Recusar não essenciais
                </button>
                <button
                  type="button"
                  onClick={openPrefs}
                  className={btnBase + ' text-gray-700 dark:text-gray-200 underline hover:text-primary-700 dark:hover:text-primary'}
                >
                  Personalizar
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className={btnBase + ' bg-primary text-gray-900 hover:bg-primary-400 border border-primary'}
                >
                  Aceitar todos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {prefsOpen && (
        <div className="fixed inset-0 z-[110] flex items-end justify-center sm:items-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/60" onClick={closePrefs} aria-hidden="true" />
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-title"
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-5 sm:p-6"
          >
            <div className="flex items-start justify-between gap-4">
              <h2 id="consent-title" className="font-heading text-lg font-bold text-gray-900 dark:text-white">
                Preferências de privacidade
              </h2>
              <button
                type="button"
                onClick={closePrefs}
                aria-label="Fechar preferências"
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            <p className="mt-2 font-body text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              Escolha o que podemos usar. Você pode mudar de ideia quando quiser pelo link
              {' '}"Preferências de cookies" no rodapé do site.
            </p>

            <div className="mt-4 space-y-3">
              <SwitchRow
                id="consent-essential"
                title="Essenciais (sempre ativos)"
                description="Guardam sua escolha de privacidade e o tema claro/escuro. Sem eles o site não funciona direito, por isso não podem ser desativados."
                checked={true}
                disabled={true}
              />
              <SwitchRow
                id="consent-thirdparty"
                title="Conteúdo de terceiros"
                description="Permite mostrar mapas do Google nas páginas de contato e de locais. O Google pode registrar seu IP e usar cookies ao carregar o mapa."
                checked={thirdParty}
                onChange={setThirdParty}
              />
              {HAS_ANALYTICS && (
                <SwitchRow
                  id="consent-analytics"
                  title="Análise de audiência"
                  description="Google Analytics, com IP anonimizado, para entendermos quais páginas ajudam e quais precisam melhorar. Não usamos para publicidade."
                  checked={analytics}
                  onChange={setAnalytics}
                />
              )}
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                onClick={rejectNonEssential}
                className={btnBase + ' border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}
              >
                Recusar não essenciais
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className={btnBase + ' border border-primary-600 text-primary-800 dark:text-primary hover:bg-primary-50 dark:hover:bg-gray-800'}
              >
                Salvar minha escolha
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className={btnBase + ' bg-primary text-gray-900 hover:bg-primary-400 border border-primary'}
              >
                Aceitar todos
              </button>
            </div>
            <p className="mt-4 font-body text-xs text-gray-500 dark:text-gray-400">
              Detalhes em{' '}
              <Link to="/politica-privacidade" onClick={closePrefs} className="underline">
                Política de Privacidade
              </Link>{' '}
              e{' '}
              <Link to="/politica-cookies" onClick={closePrefs} className="underline">
                Política de Cookies
              </Link>
              .
            </p>
          </div>
        </div>
      )}
    </>
  );
}
